(function (window, document) {
  console.log("MVT Engine: Track module loaded");

  const Utils = window.MVTUtils;

  const tokenCache = {};

  const TOKEN_CACHE_DURATION_MS = 30 * 1000;

  async function getBearerToken(publicApiBase, projectId) {
    const now = Date.now();
    const cached = tokenCache[projectId];

    if (cached && now - cached.timestamp < TOKEN_CACHE_DURATION_MS) {
      return cached.token;
    }

    const tokenUrl = `${publicApiBase}/variants/${projectId}/token`;
    const tokenResponse = await fetch(tokenUrl, {
      method: "GET",
      headers: { "Cache-Control": "no-cache" },
    });

    if (!tokenResponse.ok) {
      throw new Error(`MVT Engine: Token fetch failed with status ${tokenResponse.status}`);
    }

    const tokenPayload = await tokenResponse.json();
    const token = tokenPayload?.response?.token || tokenPayload?.token || null;

    if (!token) {
      throw new Error("MVT Engine: Token missing from response");
    }

    tokenCache[projectId] = { token, timestamp: now };
    return token;
  }

  const TRACK = {
    trackVisitor: async function (options) {
      const { publicApiBase, projectId, campaignId, variantKey, visitorId, pageUrl, referrerUrl } =
        options;

      if (!publicApiBase || !projectId || !campaignId || !variantKey) {
        console.error("MVT Engine: trackVisitor missing required parameters", {
          publicApiBase: !!publicApiBase,
          projectId: !!projectId,
          campaignId: !!campaignId,
          variantKey: !!variantKey,
        });
        return null;
      }

      if (Utils && Utils.consentBlocked && Utils.consentBlocked()) {
        (window.__MVT_PENDING_TRACK__ = window.__MVT_PENDING_TRACK__ || []).push({
          type: "visitor",
          options: options,
        });
        return null;
      }

      try {
        const finalVisitorId = visitorId || Utils.getOrCreateVisitorId(projectId);

        const finalPageUrl = Utils.appendCfUvidToUrl(pageUrl || window.location.href);
        const finalReferrerUrl = referrerUrl || document.referrer || "";

        const bearerToken = await getBearerToken(publicApiBase, projectId);

        const trackURL = `${publicApiBase}/track/visitor`;
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        const requestBody = {
          requestHeader: {
            requestId: requestId,
            clientTimestamp: new Date().toISOString(),
            userAgent: navigator.userAgent || "",
            screenResolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
            language: navigator.language || "",
            platform: navigator.platform || "",
            referrer: document.referrer || "",
            pageUrl: finalPageUrl,
          },
          body: {
            visitorId: finalVisitorId,
            campaignId: campaignId,
            variantKey: variantKey,
            projectId: projectId,
            pageUrl: finalPageUrl,
            referrerUrl: finalReferrerUrl,
            deviceType:
              typeof Utils.getDeviceType === "function" ? Utils.getDeviceType() : undefined,
          },
        };

        console.log("MVT Engine: Track Visitor request", {
          url: trackURL,
          body: requestBody.body,
        });

        const trackResponse = await fetch(trackURL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          keepalive: true,
        });

        if (!trackResponse.ok) {
          let errorBody = null;
          try {
            errorBody = await trackResponse.text();
          } catch (e) {}
          console.error("MVT Engine: Track Visitor failed", {
            status: trackResponse.status,
            error: errorBody,
          });
          return null;
        }

        const result = await trackResponse.json();
        console.log("MVT Engine: Track Visitor success", {
          isNewVisitor: result?.response?.isNewVisitor,
          variantId: result?.response?.variantId,
        });

        return result?.response || result;
      } catch (error) {
        console.error("MVT Engine: Track Visitor error", error);
        return null;
      }
    },

    trackConversion: async function (options) {
      const {
        publicApiBase,
        projectId,
        campaignId,
        visitorId,
        metricId,
        eventApiName,
        eventValue,
        eventProperties,
        pageUrl,
      } = options;

      if (!publicApiBase || !projectId || !campaignId) {
        console.error("MVT Engine: trackConversion missing required parameters", {
          publicApiBase: !!publicApiBase,
          projectId: !!projectId,
          campaignId: !!campaignId,
        });
        return null;
      }

      if (Utils && Utils.consentBlocked && Utils.consentBlocked()) {
        (window.__MVT_PENDING_TRACK__ = window.__MVT_PENDING_TRACK__ || []).push({
          type: "conversion",
          options: options,
        });
        return null;
      }

      try {
        const finalVisitorId = visitorId || Utils.getOrCreateVisitorId(projectId);

        const finalPageUrl = Utils.appendCfUvidToUrl(pageUrl || window.location.href);

        const bearerToken = await getBearerToken(publicApiBase, projectId);

        const trackURL = `${publicApiBase}/track/conversion`;
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        const requestBody = {
          requestHeader: {
            requestId: requestId,
            clientTimestamp: new Date().toISOString(),
            userAgent: navigator.userAgent || "",
            screenResolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
            language: navigator.language || "",
            platform: navigator.platform || "",
            referrer: document.referrer || "",
            pageUrl: finalPageUrl,
          },
          body: {
            visitorId: finalVisitorId,
            campaignId: campaignId,
            projectId: projectId,
            pageUrl: finalPageUrl,
            deviceType:
              typeof Utils.getDeviceType === "function" ? Utils.getDeviceType() : undefined,
          },
        };

        if (metricId) requestBody.body.metricId = metricId;
        if (eventApiName) requestBody.body.eventApiName = eventApiName;
        if (eventValue !== undefined) requestBody.body.eventValue = eventValue;
        if (eventProperties) requestBody.body.eventProperties = eventProperties;

        console.log("MVT Engine: Track Conversion request", {
          url: trackURL,
          body: requestBody.body,
        });

        const trackResponse = await fetch(trackURL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          keepalive: true,
        });

        if (!trackResponse.ok) {
          let errorBody = null;
          try {
            errorBody = await trackResponse.text();
          } catch (e) {}
          console.error("MVT Engine: Track Conversion failed", {
            status: trackResponse.status,
            error: errorBody,
          });
          return null;
        }

        const result = await trackResponse.json();
        console.log("MVT Engine: Track Conversion success", {
          isUnique: result?.response?.isUnique,
          variantId: result?.response?.variantId,
        });

        return result?.response || result;
      } catch (error) {
        console.error("MVT Engine: Track Conversion error", error);
        return null;
      }
    },

    getAssignment: async function (options) {
      const { publicApiBase, projectId, campaignId, visitorId } = options;

      if (!publicApiBase || !projectId || !campaignId) {
        console.error("MVT Engine: getAssignment missing required parameters", {
          publicApiBase: !!publicApiBase,
          projectId: !!projectId,
          campaignId: !!campaignId,
        });
        return null;
      }

      try {
        const finalVisitorId = visitorId || Utils.getOrCreateVisitorId(projectId);

        const bearerToken = await getBearerToken(publicApiBase, projectId);

        const queryParams = new URLSearchParams({
          visitorId: finalVisitorId,
          campaignId: campaignId,
        });

        const trackURL = `${publicApiBase}/track/assignment?${queryParams.toString()}`;

        console.log("MVT Engine: Get Assignment request", { url: trackURL });

        const trackResponse = await fetch(trackURL, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${bearerToken}`,
          },
        });

        if (!trackResponse.ok) {
          let errorBody = null;
          try {
            errorBody = await trackResponse.text();
          } catch (e) {}
          console.error("MVT Engine: Get Assignment failed", {
            status: trackResponse.status,
            error: errorBody,
          });
          return null;
        }

        const result = await trackResponse.json();
        console.log("MVT Engine: Get Assignment success", {
          assigned: result?.response?.assigned,
          variantKey: result?.response?.variantKey,
        });

        return result?.response || result;
      } catch (error) {
        console.error("MVT Engine: Get Assignment error", error);
        return null;
      }
    },
  };

  TRACK.warmToken = function (publicApiBase, projectId) {
    if (!publicApiBase || !projectId) return Promise.resolve(null);
    return getBearerToken(publicApiBase, projectId).catch(function () {
      return null;
    });
  };

  window.MVTTrack = TRACK;

  TRACK.flushPendingTracking = function () {
    var q = window.__MVT_PENDING_TRACK__ || [];
    window.__MVT_PENDING_TRACK__ = [];
    for (var i = 0; i < q.length; i++) {
      var item = q[i];
      try {
        if (item.type === "visitor") TRACK.trackVisitor(item.options);
        else if (item.type === "conversion") TRACK.trackConversion(item.options);
        else if (item.type === "identify" && typeof window.mvt === "function")
          window.mvt("identify", item.options);
      } catch (e) {}
    }
  };

  window.mvt = function (action, options = {}) {
    const context = window.MVTContext;

    if (action === "identify") {
      if (!options.email) {
        console.warn("MVT: identify requires an email");
        return Promise.resolve(null);
      }

      const publicApiBase = (context && context.publicApiBase) || options.publicApiBase || "";
      const projectId = (context && context.projectId) || options.projectId || "";
      const visitorId =
        (context && context.visitorId) ||
        (window.MVTUtils && window.MVTUtils.getOrCreateVisitorId(projectId)) ||
        "";

      if (!publicApiBase || !projectId) {
        console.warn("MVT: identify requires engine context (projectId, publicApiBase)");
        return Promise.resolve(null);
      }

      if (window.MVTUtils && window.MVTUtils.consentBlocked && window.MVTUtils.consentBlocked()) {
        (window.__MVT_PENDING_TRACK__ = window.__MVT_PENDING_TRACK__ || []).push({
          type: "identify",
          options: options,
        });
        return Promise.resolve(null);
      }

      const identifyUrl = `${publicApiBase}/track/identify`;
      const Utils = window.MVTUtils;
      const body = {
        visitorId: visitorId,
        projectId: projectId,
        email: options.email,
        firstName: options.firstName || "",
        lastName: options.lastName || "",
        phone: options.phone || "",
        source: options.source || "sdk",
        deviceType:
          Utils && typeof Utils.getDeviceType === "function" ? Utils.getDeviceType() : undefined,
      };

      const maskedEmail = String(options.email).replace(
        /^(.)(.*)(@.*)$/,
        function (_all, first, middle, domain) {
          return first + middle.replace(/./g, "*") + domain;
        },
      );
      console.log("MVT Engine: Identify request", {
        email: maskedEmail,
        projectId,
        source: body.source,
      });

      return (async () => {
        try {
          const Utils = window.MVTUtils;
          const tokenUrl = `${publicApiBase}/variants/${projectId}/token`;
          const tokenRes = await fetch(tokenUrl, {
            method: "GET",
            headers: { "Cache-Control": "no-cache" },
          });
          if (!tokenRes.ok) throw new Error("Token fetch failed");
          const tokenPayload = await tokenRes.json();
          const token = tokenPayload?.response?.token || tokenPayload?.token;

          const res = await fetch(identifyUrl, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(body),
            keepalive: true,
          });
          if (!res.ok) {
            const errText = await res.text().catch(() => "");
            console.warn("MVT Engine: Identify failed", { status: res.status, error: errText });
            return null;
          }
          const result = await res.json();
          console.log("MVT Engine: Identify success", { leadId: result?.response?.leadId });
          return result?.response || result;
        } catch (err) {
          console.warn("MVT Engine: Identify error", err);
          return null;
        }
      })();
    }

    if (!context) {
      console.warn("MVT: No tracking context available. Has the engine loaded?");
      return Promise.resolve(null);
    }

    if (action === "conversion") {
      let metricId = options.metricId;
      if (!metricId && options.eventApiName && window.MVTResolveManualMetric) {
        const armed = window.MVTResolveManualMetric(options.eventApiName);
        if (armed && armed.metric) {
          metricId = armed.metric.id;
        }
      }

      return TRACK.trackConversion({
        publicApiBase: context.publicApiBase,
        projectId: context.projectId,
        campaignId: context.campaignId,
        visitorId: context.visitorId,
        metricId: metricId,
        eventApiName: options.eventApiName,
        eventValue: options.eventValue,
        eventProperties: options.eventProperties,
        pageUrl: options.pageUrl,
      });
    }

    if (action === "visitor") {
      return TRACK.trackVisitor({
        publicApiBase: context.publicApiBase,
        projectId: context.projectId,
        campaignId: options.campaignId || context.campaignId,
        variantKey: options.variantKey || context.variantKey,
        visitorId: context.visitorId,
        pageUrl: options.pageUrl,
        referrerUrl: options.referrerUrl,
      });
    }

    if (action === "assignment") {
      return TRACK.getAssignment({
        publicApiBase: context.publicApiBase,
        projectId: context.projectId,
        campaignId: options.campaignId || context.campaignId,
        visitorId: context.visitorId,
      });
    }

    console.warn(
      `MVT: Unknown action "${action}". Use "conversion", "visitor", "identify", or "assignment".`,
    );
    return Promise.resolve(null);
  };
})(window, document);
